/* eslint-disable no-param-reassign */
import React, {useState} from 'react';

import {useTermsOfAgreement} from 'presentation/hooks';
import {cleanFileName} from 'presentation/utils/cleanFileName';

import {translations} from 'presentation/translations-new';
import {SendingFilesSchema} from '../../validations';
import {ALLOWED_EXTENSIONS} from '../../constants';
import {UseFileHandlerProps} from './types';
import {IFile} from '../../types';

const {
	validations: {onlyOneFile},
} = translations['pt-br'].others.uploadFiles;

export const useFileHandler = ({
	isUploading,
	shouldShowTermsModal,
	onChangeFile,
	removeFile,
	setExternaErrorMessage,
	values,
}: UseFileHandlerProps) => {
	const [fileIdState, setFileIdState] = useState('');
	const [errorState, setErrorState] = useState<string | null>(null);
	const [isConfirmActionModalOpenState, setIsConfirmActionModalOpenState] = useState(false);
	const [isTermsModalOpenState, setIsTermsModalOpenState] = useState(false);
	const [isOnDropState, setIsOnDropState] = useState(false);
	const [isDragAndDropState, setIsDragAndDropState] = useState(false);
	const [openDuplicateModalState, setOpenDuplicateModalState] = useState(false);

	const {hasAcceptedTerms} = useTermsOfAgreement();
	const showTermsModal =
		shouldShowTermsModal.show && !hasAcceptedTerms(shouldShowTermsModal.termName);

	const allowedExtensions = ALLOWED_EXTENSIONS.join(', ');

	const checkDuplicateFile = (fileToCheck: File, files: IFile[]) => {
		const isDuplicate =
			files && files.some(f => f.filename === cleanFileName(fileToCheck.name));
		if (isDuplicate) {
			setOpenDuplicateModalState(true);
		}
	};

	const validateFile = (file: File) => {
		const result = SendingFilesSchema.safeParse(file);
		if (!result.success) {
			setErrorState(result.error.issues[0].message);
			return false;
		}
		return true;
	};

	const handleToggleTermsModal = () => {
		setIsTermsModalOpenState(prev => !prev);
	};

	const handleToggleConfirmActionModal = () => {
		setIsConfirmActionModalOpenState(prev => !prev);
	};

	const handleToggleConfirmAction = (id: string) => {
		setFileIdState(id);
	};

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setErrorState(null);
		setExternaErrorMessage(null);
		const {files} = e.target;

		if (!files) {
			return;
		}

		const fileIsValid = validateFile(files[0]);

		if (!fileIsValid) return;

		const filesArray = Array.from(files);
		onChangeFile(filesArray);

		checkDuplicateFile(files[0], values);

		e.target.value = '';
		setIsOnDropState(false);
	};

	const handleOnDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setErrorState(null);
		setIsDragAndDropState(true);
		setExternaErrorMessage(null);

		if (isUploading) {
			setIsOnDropState(false);
			return;
		}

		if (showTermsModal) {
			handleToggleTermsModal();
			setIsOnDropState(false);
			return;
		}

		const {files} = event.dataTransfer;

		if (!files) {
			return;
		}

		const filesArray = Array.from(files);

		if (filesArray.length > 1) {
			setErrorState(onlyOneFile);
			setIsOnDropState(false);
			return;
		}

		const fileIsValid = validateFile(files[0]);

		if (!fileIsValid) return;

		onChangeFile(filesArray);

		checkDuplicateFile(files[0], values);

		setIsOnDropState(false);
		setIsDragAndDropState(false);
	};

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsOnDropState(true);
		setErrorState(null);
		setExternaErrorMessage(null);
	};

	const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsOnDropState(false);
		setErrorState(null);
		setExternaErrorMessage(null);
	};

	const handleUploadByClick = (e: React.MouseEvent<HTMLInputElement>) => {
		setErrorState(null);
		setExternaErrorMessage(null);
		setIsDragAndDropState(false);

		if (showTermsModal) {
			e.preventDefault();
			handleToggleTermsModal();
		}
	};

	const handleConfirmModal = () => {
		removeFile(fileIdState);
		handleToggleConfirmActionModal();
	};

	return {
		errorState,
		isConfirmActionModalOpenState,
		isTermsModalOpenState,
		isOnDropState,
		allowedExtensions,
		handleToggleConfirmActionModal,
		handleToggleConfirmAction,
		handleOnChange,
		handleOnDrop,
		handleDragOver,
		handleDragLeave,
		handleUploadByClick,
		handleConfirmModal,
		setIsConfirmActionModalOpenState,
		setIsTermsModalOpenState,
		openDuplicateModalState,
		isDragAndDropState,
		setOpenDuplicateModalState,
	};
};
